import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Check, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const OnboardingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [partnerId, setPartnerId] = useState('');
  const [loading, setLoading] = useState(false);

  // Form data for all steps
  const [step1Data, setStep1Data] = useState({
    company_name: '',
    company_street: '',
    company_zip: '',
    company_city: '',
    company_country: 'Deutschland',
    company_legal_form: '',
    company_phone: '',
    company_website: ''
  });

  const [step2Data, setStep2Data] = useState({
    contact_first_name: '',
    contact_last_name: '',
    contact_phone: ''
  });

  const [step3Data, setStep3Data] = useState({
    billing_vat_id: '',
    billing_email: ''
  });

  const [step4Data, setStep4Data] = useState({
    agreements_accepted: false,
    privacy_accepted: false,
    data_permission_accepted: false
  });

  const [step5Data, setStep5Data] = useState({
    password: '',
    password_confirm: ''
  });

  useEffect(() => {
    const id = searchParams.get('partner_id');
    if (!id) {
      navigate('/partner/registrieren');
      return;
    }
    setPartnerId(id);
  }, [searchParams, navigate]);

  const saveStep = async (step, data) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/onboarding/step${step}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ partner_id: partnerId, ...data })
      });

      if (!response.ok) {
        throw new Error('Speichern fehlgeschlagen');
      }

      return true;
    } catch (err) {
      alert(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleStep1Next = async () => {
    if (!step1Data.company_name || !step1Data.company_street || !step1Data.company_zip || !step1Data.company_city) {
      alert('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    const success = await saveStep(1, step1Data);
    if (success) setCurrentStep(2);
  };

  const handleStep2Next = async () => {
    if (!step2Data.contact_first_name || !step2Data.contact_last_name) {
      alert('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    const success = await saveStep(2, step2Data);
    if (success) setCurrentStep(3);
  };

  const handleStep3Next = async () => {
    const success = await saveStep(3, step3Data);
    if (success) setCurrentStep(4);
  };

  const handleStep4Next = async () => {
    if (!step4Data.agreements_accepted || !step4Data.privacy_accepted || !step4Data.data_permission_accepted) {
      alert('Bitte stimmen Sie allen Bedingungen zu');
      return;
    }
    const success = await saveStep(4, { 
      terms_version: 'v1.0',
      privacy_version: 'v1.0',
      accepted_ip: null  // Could be fetched from client if needed
    });
    if (success) setCurrentStep(5);
  };

  const handleStep5Finish = async () => {
    if (!step5Data.password || step5Data.password.length < 10) {
      alert('Passwort muss mindestens 10 Zeichen lang sein');
      return;
    }
    if (step5Data.password !== step5Data.password_confirm) {
      alert('Passwörter stimmen nicht überein');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/onboarding/step5`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ partner_id: partnerId, password: step5Data.password })
      });
      
      if (!response.ok) {
        throw new Error('Speichern fehlgeschlagen');
      }
      
      const data = await response.json();
      
      // Store JWT token for authentication
      if (data.token) {
        localStorage.setItem('partner_token', data.token);
      }
      localStorage.setItem('partner_id', partnerId);
      
      // Redirect to dashboard
      navigate(`/portal/dashboard?partner_id=${partnerId}`);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const STEPS = [
    { number: 1, title: 'Unternehmen' },
    { number: 2, title: 'Ansprechpartner' },
    { number: 3, title: 'Abrechnung' },
    { number: 4, title: 'Verträge' },
    { number: 5, title: 'Passwort' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-6">
        {/* Progress Header */}
        <div className="mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">Partner Onboarding</h1>
          
          {/* Step Indicator */}
          <div className="flex items-center justify-between mb-8">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg mb-2 transition-all ${
                    step.number < currentStep 
                      ? 'bg-brand-green text-white' 
                      : step.number === currentStep 
                      ? 'bg-brand-green text-white ring-4 ring-brand-green/20' 
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step.number < currentStep ? <Check className="w-6 h-6" /> : step.number}
                  </div>
                  <span className="text-xs font-medium text-gray-600">{step.title}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 rounded ${
                    step.number < currentStep ? 'bg-brand-green' : 'bg-gray-200'
                  }`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white border border-gray-200 rounded-3xl p-10 shadow-lg">
          {currentStep === 1 && (
            <Step1Content data={step1Data} setData={setStep1Data} onNext={handleStep1Next} loading={loading} />
          )}
          {currentStep === 2 && (
            <Step2Content data={step2Data} setData={setStep2Data} onNext={handleStep2Next} onBack={() => setCurrentStep(1)} loading={loading} />
          )}
          {currentStep === 3 && (
            <Step3Content data={step3Data} setData={setStep3Data} onNext={handleStep3Next} onBack={() => setCurrentStep(2)} loading={loading} />
          )}
          {currentStep === 4 && (
            <Step4Content data={step4Data} setData={setStep4Data} onNext={handleStep4Next} onBack={() => setCurrentStep(3)} loading={loading} />
          )}
          {currentStep === 5 && (
            <Step5Content data={step5Data} setData={setStep5Data} onFinish={handleStep5Finish} onBack={() => setCurrentStep(4)} loading={loading} />
          )}
        </div>
      </div>
    </div>
  );
};

// Step 1: Company Data
const Step1Content = ({ data, setData, onNext, loading }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Unternehmensdaten</h2>
      <p className="text-gray-600">Bitte geben Sie Ihre Firmendaten ein</p>
    </div>

    <div className="grid md:grid-cols-2 gap-6">
      <div className="md:col-span-2">
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Firmenname *
        </label>
        <input
          type="text"
          required
          value={data.company_name}
          onChange={(e) => setData({ ...data, company_name: e.target.value })}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
          placeholder="Ihre Firma GmbH"
        />
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Straße + Hausnummer *
        </label>
        <input
          type="text"
          required
          value={data.company_street}
          onChange={(e) => setData({ ...data, company_street: e.target.value })}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
          placeholder="Musterstraße 123"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          PLZ *
        </label>
        <input
          type="text"
          required
          value={data.company_zip}
          onChange={(e) => setData({ ...data, company_zip: e.target.value })}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
          placeholder="12345"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Ort *
        </label>
        <input
          type="text"
          required
          value={data.company_city}
          onChange={(e) => setData({ ...data, company_city: e.target.value })}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
          placeholder="Musterstadt"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Rechtsform
        </label>
        <input
          type="text"
          value={data.company_legal_form}
          onChange={(e) => setData({ ...data, company_legal_form: e.target.value })}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
          placeholder="z.B. GmbH"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Telefon
        </label>
        <input
          type="tel"
          value={data.company_phone}
          onChange={(e) => setData({ ...data, company_phone: e.target.value })}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
          placeholder="+49 123 456789"
        />
      </div>
    </div>

    <div className="flex justify-end pt-6">
      <Button
        onClick={onNext}
        disabled={loading}
        size="lg"
        className="bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-xl font-semibold"
      >
        {loading ? 'Speichern...' : 'Weiter'}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  </div>
);

// Step 2: Contact Person
const Step2Content = ({ data, setData, onNext, onBack, loading }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Ansprechpartner</h2>
      <p className="text-gray-600">Ihre Kontaktdaten</p>
    </div>

    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Vorname *
        </label>
        <input
          type="text"
          required
          value={data.contact_first_name}
          onChange={(e) => setData({ ...data, contact_first_name: e.target.value })}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
          placeholder="Max"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Nachname *
        </label>
        <input
          type="text"
          required
          value={data.contact_last_name}
          onChange={(e) => setData({ ...data, contact_last_name: e.target.value })}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
          placeholder="Mustermann"
        />
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Telefon/Mobil
        </label>
        <input
          type="tel"
          value={data.contact_phone}
          onChange={(e) => setData({ ...data, contact_phone: e.target.value })}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
          placeholder="+49 170 1234567"
        />
      </div>
    </div>

    <div className="flex justify-between pt-6">
      <Button onClick={onBack} variant="outline" className="border-2 border-gray-300">
        <ArrowLeft className="w-5 h-5 mr-2" />
        Zurück
      </Button>
      <Button
        onClick={onNext}
        disabled={loading}
        size="lg"
        className="bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-xl font-semibold"
      >
        {loading ? 'Speichern...' : 'Weiter'}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  </div>
);

// Step 3: Billing
const Step3Content = ({ data, setData, onNext, onBack, loading }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Abrechnung</h2>
      <p className="text-gray-600">Optionale Angaben für die Rechnungsstellung</p>
    </div>

    <div className="bg-brand-green/5 border border-brand-green/20 rounded-2xl p-6 mb-6">
      <p className="text-sm text-gray-700">
        <strong>Abo:</strong> ab 29,99€ inkl. MwSt. pro Monat
      </p>
    </div>

    <div className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Umsatzsteuer-ID
        </label>
        <input
          type="text"
          value={data.billing_vat_id}
          onChange={(e) => setData({ ...data, billing_vat_id: e.target.value })}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
          placeholder="DE123456789"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Rechnungs-E-Mail
        </label>
        <input
          type="email"
          value={data.billing_email}
          onChange={(e) => setData({ ...data, billing_email: e.target.value })}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
          placeholder="buchhaltung@firma.de"
        />
      </div>
    </div>

    <div className="flex justify-between pt-6">
      <Button onClick={onBack} variant="outline" className="border-2 border-gray-300">
        <ArrowLeft className="w-5 h-5 mr-2" />
        Zurück
      </Button>
      <Button
        onClick={onNext}
        disabled={loading}
        size="lg"
        className="bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-xl font-semibold"
      >
        {loading ? 'Speichern...' : 'Weiter'}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  </div>
);

// Step 4: Agreements (wird in nächster Nachricht fortgesetzt)
const Step4Content = ({ data, setData, onNext, onBack, loading }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Verträge & Einwilligungen</h2>
      <p className="text-gray-600">Bitte lesen und akzeptieren Sie die Bedingungen</p>
    </div>

    {/* Partnerbedingungen */}
    <div className="border border-gray-200 rounded-2xl p-6 max-h-96 overflow-y-auto bg-gray-50">
      <h3 className="text-lg font-bold text-gray-900 mb-4">Partnerbedingungen v1.0</h3>
      <div className="text-sm text-gray-700 leading-relaxed space-y-3">
        <p><strong>1. Portalzugang</strong></p>
        <p>ab 29,99€ inkl. MwSt./Monat. Zugang nur bei aktivem Abo.</p>
        
        <p><strong>2. Provision</strong></p>
        <p>Basiert auf tatsächlich reguliertem Netto-Grundhonorar (nach Kürzungen). Auszahlung innerhalb 7 Tage nach Zahlungseingang.</p>
        
        <p><strong>3. Staffelmodell</strong></p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Start: 30%</li>
          <li>40% bei ≥ 10 Gutachten/Monat über 3 aufeinanderfolgende Monate</li>
          <li>50% bei ≥ 20 Gutachten/Monat über 3 aufeinanderfolgende Monate</li>
        </ul>
        <p>Gutachten zählt, wenn Fall als abgeschlossen markiert wurde. Upgrade aktiv nach 3 vollen Monaten.</p>
        
        <p><strong>4. Wahrheits-/Mitwirkungspflicht</strong></p>
        <p>Vollständige Angaben nach bestem Wissen. Vorschäden dürfen nicht bewusst verschwiegen werden. Bei Falschangaben kann Provision entfallen und/oder Fall abgelehnt werden.</p>
        
        <p><strong>5. Keine Mindestprovision</strong></p>
        <p>Wenn keine Zahlung eingeht, entfällt die Provision.</p>
      </div>
    </div>

    {/* Checkboxen */}
    <div className="space-y-4">
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={data.agreements_accepted}
          onChange={(e) => setData({ ...data, agreements_accepted: e.target.checked })}
          className="mt-1 w-5 h-5 text-brand-green border-gray-300 rounded focus:ring-brand-green"
        />
        <span className="text-sm text-gray-700">
          Ich habe die <a href="/partnerbedingungen" target="_blank" className="text-brand-green hover:text-brand-green-dark font-semibold">Partnerbedingungen v1.0</a> gelesen und akzeptiere diese.
        </span>
      </label>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={data.data_permission_accepted}
          onChange={(e) => setData({ ...data, data_permission_accepted: e.target.checked })}
          className="mt-1 w-5 h-5 text-brand-green border-gray-300 rounded focus:ring-brand-green"
        />
        <span className="text-sm text-gray-700">
          Ich bestätige, dass ich berechtigt bin, die übermittelten Daten und Fotos an Dein-Gutachten-Direkt weiterzugeben.
        </span>
      </label>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={data.privacy_accepted}
          onChange={(e) => setData({ ...data, privacy_accepted: e.target.checked })}
          className="mt-1 w-5 h-5 text-brand-green border-gray-300 rounded focus:ring-brand-green"
        />
        <span className="text-sm text-gray-700">
          Ich habe die <a href="/datenschutz" target="_blank" className="text-brand-green hover:text-brand-green-dark font-semibold">Datenschutzerklärung</a> gelesen und akzeptiere diese.
        </span>
      </label>
    </div>

    <div className="flex justify-between pt-6">
      <Button onClick={onBack} variant="outline" className="border-2 border-gray-300">
        <ArrowLeft className="w-5 h-5 mr-2" />
        Zurück
      </Button>
      <Button
        onClick={onNext}
        disabled={loading || !data.agreements_accepted || !data.privacy_accepted || !data.data_permission_accepted}
        size="lg"
        className="bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-xl font-semibold disabled:opacity-50"
      >
        {loading ? 'Speichern...' : 'Weiter'}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  </div>
);

// Step 5: Password
const Step5Content = ({ data, setData, onFinish, onBack, loading }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Passwort setzen</h2>
      <p className="text-gray-600">Wählen Sie ein sicheres Passwort für Ihren Portal-Zugang</p>
    </div>

    <div className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Passwort * (mindestens 10 Zeichen)
        </label>
        <input
          type="password"
          required
          value={data.password}
          onChange={(e) => setData({ ...data, password: e.target.value })}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
          placeholder="••••••••••"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Passwort wiederholen *
        </label>
        <input
          type="password"
          required
          value={data.password_confirm}
          onChange={(e) => setData({ ...data, password_confirm: e.target.value })}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
          placeholder="••••••••••"
        />
      </div>

      {data.password && data.password_confirm && data.password !== data.password_confirm && (
        <p className="text-sm text-red-600">Passwörter stimmen nicht überein</p>
      )}
    </div>

    <div className="flex justify-between pt-6">
      <Button onClick={onBack} variant="outline" className="border-2 border-gray-300">
        <ArrowLeft className="w-5 h-5 mr-2" />
        Zurück
      </Button>
      <Button
        onClick={onFinish}
        disabled={loading}
        size="lg"
        className="bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-xl font-semibold"
      >
        {loading ? 'Speichern...' : 'Onboarding abschließen'}
        <Check className="w-5 h-5 ml-2" />
      </Button>
    </div>
  </div>
);

export default OnboardingPage;
